import React, { useState } from "react";
import { Store, Package, Layers, Printer, Check, LayoutDashboard } from "lucide-react";
import { plansData } from "../../utils/dummydata";
import { theme } from "../../utils/theme";
import { getUserType } from "../../utils/utils";
import { useNavigate } from "react-router-dom";

const PricingSection = () => {
    const [activeTab, setActiveTab] = useState("printShop");
    const plan = plansData[activeTab];
    const navigate = useNavigate();

    const t = theme[getUserType(activeTab)];

    const primaryIndexes = getPrimaryIndexes(activeTab, plan.key);


    const buttons = [
        { key: "printShop", label: "On-Demand", icon: Store },
        { key: "specialist", label: "Specialist", icon: Package },
        { key: "supplier", label: "Suppliers", icon: Layers },
    ];

    return (
        <section className="bg-[#f8f8fa]">
            <div className="max-w-[1200px] mx-auto px-[16px] md:px-[40px] py-[56px] md:py-[80px]">

                {/* Header */}
                <div className="text-center mb-[32px]">
                    <p className="font-semibold text-[13px] text-[#ff5323] uppercase mb-[8px]">
                        Pricing
                    </p>
                    <h2 className="font-semibold text-[28px] md:text-[36px] text-[#212326] mb-[12px]">
                        Three plans. One for every kind of print business.
                    </h2>
                    <p className="text-[16px] text-[#717182] max-w-[520px] mx-auto">
                        Pick your business type and see exactly what you get.
                    </p>
                </div>

                {/* Toggle Buttons */}
                <div className="flex bg-[#f4f5f6] rounded-[12px] p-[4px] gap-[4px] max-w-[520px] mx-auto mb-[32px]">
                    {buttons.map((btn) => {
                        const Icon = btn.icon;
                        const isActive = activeTab === btn.key;

                        return (
                            <button
                                key={btn.key}
                                onClick={() => setActiveTab(btn.key)}
                                className={`flex-1 flex items-center justify-center gap-[6px] py-[12px] rounded-[10px] font-semibold text-[13px] transition-all ${isActive
                                    ? "bg-white text-[#212326] shadow-[0px_1px_4px_rgba(0,0,0,0.08)]"
                                    : "bg-transparent text-[#717182] hover:text-[#212326]"
                                    }`}
                            >
                                <Icon size={15} />
                                <span className="hidden sm:inline">{btn.label}</span>
                            </button>
                        );
                    })}
                </div>

                {/* INFO BANNER (THIS IS WHAT YOU SAID WAS MISSING) */}
                <div
                    className="flex items-start gap-[12px] rounded-[12px] px-[16px] py-[14px] border max-w-[480px] mx-auto mb-[24px] bg-[#fff8f5] border-[#ffe4db]"
                    style={{
                        backgroundColor: t.bg,
                        borderColor: t.border,
                    }}
                >
                    <div
                        className="w-[36px] h-[36px] rounded-[10px] flex items-center justify-center shrink-0 bg-[#ff5323]"
                        style={{ backgroundColor: t.iconBg }}
                    >
                        {/* icon */}
                        <LayoutDashboard className="w-[16px] h-[16px] text-white" />

                    </div>

                    <div>
                        <p className="font-semibold text-[13px] text-[#212326] mb-[2px]">
                            {activeTab === "specialist"
                                ? "How it works for Specialist Print Shops"
                                : activeTab === "printShop"
                                    ? "How it works for On-Demand Print Shops"
                                    : "How it works for Print Suppliers"}
                        </p>

                        <p className="font-medium text-[12px] text-[#717182] leading-[1.5]">
                            {activeTab === "specialist" &&
                                "For t-shirts, mugs, bags, signage, and other specialist printing. Customers send enquiries and they land straight to your dashboard and inbox. "}

                            {activeTab === "printShop" &&
                                "For everyday on-demand print shops. Customers upload files that land in your order directly in your dashboard. You also get the same enquiries dashboard as specialist and supplier stores."}

                            {activeTab === "supplier" &&
                                "For Print machinery, papers, inks, and other print supplies. Get listed, manage enquiries in your dashboard, and have every one forwarded to your email."}
                        </p>
                    </div>
                </div>


                {/* Card */}
                <div className="max-w-[640px] mx-auto">
                    <div className="rounded-[16px] border-2 border-[#e4e5e7] bg-white overflow-hidden hover:border-[#c0c2c6] transition-all">

                        <div className="p-[24px] flex flex-col md:flex-row gap-[20px]">

                            {/* Left */}
                            <div className="flex-1 flex flex-col gap-[12px]">
                                <div className="w-[44px] h-[44px] rounded-[12px] bg-[#fff0ec] flex items-center justify-center"
                                    style={{ backgroundColor: t.accentBg }}
                                >
                                    <Printer className={`text-[${t.primary}]`} />
                                </div>

                                <div>
                                    <p className="font-semibold text-[18px] text-[#212326] mb-[2px]">
                                        {plan.title}
                                    </p>

                                    <div className="flex items-baseline gap-[4px] mb-[8px]">
                                        <span className="font-semibold text-[32px] text-[#212326]">
                                            {plan.price}
                                        </span>
                                        <span className="text-[14px] text-[#717182]">/month</span>
                                    </div>

                                    <p className="text-[13px] text-[#717182] leading-[1.5]">
                                        {plan.description}
                                    </p>
                                </div>

                                <button
                                    onClick={() => navigate("/onboarding/plan")}
                                    className="text-center w-full py-[12px] rounded-[10px] font-semibold text-[14px] text-white mt-[4px]"
                                    style={{ backgroundColor: t.primary }}
                                >
                                    Get Started
                                </button>
                            </div>

                            {/* Right Features */}
                            <div className="flex-1 flex flex-col gap-[10px] md:pt-[8px]">

                                {plan.features.map((f, i) => {
                                    const isPrimary = primaryIndexes.includes(i);

                                    return (
                                        <div key={i} className="flex items-center gap-[8px]">
                                            <div
                                                className="w-[18px] h-[18px] rounded-full flex items-center justify-center"
                                                style={{
                                                    backgroundColor: isPrimary ? t.primary : "#e8f8e8",
                                                }}
                                            >
                                                <Check
                                                    size={10}
                                                    className={isPrimary ? "text-white" : "text-[#2ecc71]"}
                                                />
                                            </div>
                                            <span className="text-[13px] text-[#212326]">
                                                {f}
                                            </span>
                                        </div>
                                    );
                                })}

                                {activeTab === "supplier" ? (
                                    <div className="flex items-center gap-[6px] mt-[4px] px-[10px] py-[6px] rounded-[8px] bg-[#f0fdf8]">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                            stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-mail w-[12px] h-[12px] text-[#16a34a]">
                                            <rect width="20" height="16" x="2" y="4" rx="2"></rect>
                                            <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"></path>
                                        </svg>
                                        <span className="font-medium text-left text-[11px] text-[#16a34a]">Supplier dashboard + enquiries forwarded to your email</span>
                                    </div>
                                )
                                    : activeTab === "specialist" ? (
                                        <div className="flex items-center gap-[6px] mt-[4px] px-[10px] py-[6px] rounded-[8px] bg-[#FAF5FF]">
                                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                                stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-layers w-[12px] h-[12px] text-[#7C3AED]">
                                                <path d="M12.83 2.18a2 2 0 0 0-1.66 0L2.6 6.08a1 1 0 0 0 0 1.83l8.58 3.91a2 2 0 0 0 1.66 0l8.58-3.9a1 1 0 0 0 0-1.83z"></path>
                                                <path d="M2 12a1 1 0 0 0 .58.91l8.6 3.91a2 2 0 0 0 1.65 0l8.58-3.9A1 1 0 0 0 22 12"></path>
                                                <path d="M2 17a1 1 0 0 0 .58.91l8.6 3.91a2 2 0 0 0 1.65 0l8.58-3.9A1 1 0 0 0 22 17"></path>
                                            </svg>
                                            <span className="font-medium text-left text-[11px] text-[#7C3AED]">Enquiries dashboard + forwarded to your email</span>
                                        </div>
                                    ) : null
                                }

                            </div>
                        </div>

                    </div>
                </div>
            </div>
        </section>
    );
};


export default PricingSection;

const getPrimaryIndexes = (activeTab, planKey) => {
    if (activeTab === "printShop") return [1, 2, 3, 4];
    if (activeTab === "specialist") return [1, 2, 3];
    if (activeTab === "supplier") return [1, 2];
    return [];
};